import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Button, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import dayjs from "dayjs";
import useScreenSize from "../../../hooks/useScreenSize";

import { useLazyPrintEventLogsListQuery } from "../../../middleware/logsApi";
import { useLazyGetAllMagazineEventsQuery } from "../../../middleware/magazineApi";
import { useGetUserGridSettingQuery, useLazyGetUserGridSettingQuery } from "../../../middleware/usersApi";
import { eventsOptions, bgColors } from "./options";
import { formFields, getConfigFormModalSelect } from "./form-config";
import { useAction } from "../../../hooks/actions";
import { getColumns } from "./columns";
import FormItem from "../../components/FormItem";
import { useAppSelector } from "../../../hooks/redux";
import { getBgColor, getBorderColor, getFiltersObject, getMessagefilter } from "./helpers";
import { ModalSelectColumnsServer } from "../../components/Modals/ModalSelectColumnsServer";
import SettingsIcon from "../../components/icons/ButtonIcons/SettingsIcon";
import PrintIcon from "../../components/icons/ButtonIcons/PrintIcon";
import DownloadIcon from "../../components/icons/ButtonIcons/DownloadIcon";
import ClearFilterIcon from "../../components/icons/ButtonIcons/ClearFilterIcon";
import { TooltipProps } from "../../../helpers/materialUITheme";
import { base64toPDF, getTableHieght, printBase64 } from "../../../helpers/tableHelper";
import { formatAddress } from "../../../helpers/helper";
import SelectSimpleWithClientSearch from "../../components/SelectSimple/SelectSimpleWithClientSearch";
import { dateFormatWithTimeWithoutA } from "../../../constants";
import { cancelCauses } from "../../components/Modals/ModalConfirmPageWithCheckbox";
import { EnhancedTableHead } from "../ObjectsList/TableBasic/Filters";
import TableBasic from "../ObjectsList/TableBasic";
import "./styles/style.css";

export const MagazineEvents = () => {
  const { t } = useTranslation();
  const screenSize = useScreenSize();
  const {
    reactiondateRangeStorage,
    protectionObjectIdStorage,
    searchAddressStorage,
    searchmessageClassStorage,
    searchplumeStorage,
    searchtriggerCauseStorage,
    searchcontrolConsoleNameStorage,
    searchresponsibleUserStorage,
    searchexecutiveUserStorage,
    searchreasonTypeStorage,
    showOnlyFireMessagesPerActiveSessionStorage,
    channelStorage,
    searchMessageCodeNameStorage,
    lineStorage,
    chapterStorage,
  } = JSON.parse(sessionStorage.getItem("magazineEventsListSearchParams")) || {};
  const [getParams, setParams] = useSearchParams();

  const { control, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      [formFields.searchParams]: {
        [formFields.reactionDateTime]: reactiondateRangeStorage || "",
        [formFields.facilityId]: getParams.get("facilityName") || protectionObjectIdStorage || "",
        [formFields.searchAddress]: searchAddressStorage || "",
        [formFields.messageClass]: searchmessageClassStorage || "",
        [formFields.plume]: searchplumeStorage || "",
        [formFields.triggerCause]: searchtriggerCauseStorage || "",
        [formFields.reasonType]: searchreasonTypeStorage || "",
        [formFields.controlConsoleName]: searchcontrolConsoleNameStorage || "",
        [formFields.responsibleCustomerUser]: searchresponsibleUserStorage || "",
        [formFields.executiveUser]: searchexecutiveUserStorage || "",
        [formFields.channel]: channelStorage || "",
        [formFields.line]: lineStorage || "",
        [formFields.chapter]: chapterStorage || "",
        [formFields.messageCodeName]: searchMessageCodeNameStorage || "",
        [formFields.showOnlyFireMessagesPerActiveSession]:
          getMessagefilter(getParams.get("status") || showOnlyFireMessagesPerActiveSessionStorage) || -1,
      },
    },
  });

  const params = useWatch({
    control,
    name: formFields.searchParams,
  });

  const { allMagazineEvents } = useAppSelector((state) => state.magazine);
  const { setIsErrorRequest } = useAction();
  const [printLoading, setPrintLoading] = useState(false);
  const [openSelectModal, setOpenSelectModal] = useState(false);
  const [pageNumber, changePageNumber] = useState(0);
  const [loading, setLoading] = useState(false);
  const refreshIntervalRef = useRef(null);

  const { data: gridColumns } = useGetUserGridSettingQuery({ gridSettingName: "EventLogJournal" });
  const [getUserGrid] = useLazyGetUserGridSettingQuery();
  const [getAllMagazineEvents, { error, isLoading }] = useLazyGetAllMagazineEventsQuery();
  const [printLogsList] = useLazyPrintEventLogsListQuery();

  function writeSearchDataToSessionStorage(newSearchParam, searchValue) {
    const getStorage = JSON.parse(sessionStorage.getItem("magazineEventsListSearchParams")) || {};
    const searchParamsForLocalStorage = {
      ...getStorage,
      [newSearchParam]: searchValue,
    };
    sessionStorage.setItem("magazineEventsListSearchParams", JSON.stringify(searchParamsForLocalStorage));
  }
  function clearFiltersHandler(isClearSearchParams = true) {
    changePageNumber(0);
    reset({
      [formFields.searchParams]: {
        [formFields.reactionDateTime]: "",
        [formFields.facilityId]: "",
        [formFields.searchAddress]: "",
        [formFields.messageClass]: "",
        [formFields.messageCodeName]: "",
        [formFields.plume]: "",
        [formFields.triggerCauseDisp]: "",
        [formFields.reasonType]: "",
        [formFields.controlConsoleName]: "",
        [formFields.responsibleCustomerUser]: "",
        [formFields.executiveUser]: "",
        [formFields.channel]: "",
        [formFields.line]: "",
        [formFields.chapter]: "",
        [formFields.showOnlyFireMessagesPerActiveSession]: -1,
      },
    });
    isClearSearchParams && setParams({});
    sessionStorage.removeItem("magazineEventsListSearchParams");
  }

  const isAnyModalOpen = useMemo(() => {
    return openSelectModal;
  }, [openSelectModal]);

  const rows = useMemo(() => {
    const { data } = allMagazineEvents || {};

    return data?.length
      ? data.map(
          ({
            protectionObjectAddress,
            messageTime,
            protectedObjectName,
            reasonType,
            line,
            chapter,
            channel,
            ...item
          }) => {
            const formattedAddress = formatAddress(protectionObjectAddress);
            return {
              reactionDateTime: dayjs(messageTime).format(dateFormatWithTimeWithoutA),
              facility: protectedObjectName,
              facilityAddress: formattedAddress,
              reasonType: cancelCauses.find(({ id }) => id === reasonType)?.name || "",
              line: line === 0 ? "" : line,
              chapter: chapter === 0 ? "" : chapter,
              channel: channel === 0 ? "" : channel,
              ...item,
            };
          }
        )
      : [];
  }, [allMagazineEvents]);

  const columnsFromStorage = useMemo(() => {
    const storageData = gridColumns?.columnSettings || [];
    return storageData?.reduce((acc, { name, visible }) => ({ ...acc, [name]: visible }), {});
  }, [gridColumns]);

  const columns = useMemo(
    () =>
      getColumns({
        control,
        columnsFromStorage,
        writeSearchDataToSessionStorage,
      }),

    [columnsFromStorage, params]
  );

  const recordsCount = useMemo(() => allMagazineEvents && allMagazineEvents?.recordsCount, [allMagazineEvents]);

  const handleOpenSelectColumnsModal = () => {
    setOpenSelectModal(true);
  };

  const refreshTable = (isZeroPage) => {
    const filters = getFiltersObject(params);
    getAllMagazineEvents({
      ...filters,
      pageNumber: isZeroPage ? 0 : pageNumber,
    });
  };

  const refreshIntervalList = () => {
    // Очищаем предыдущий интервал
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    if (!isAnyModalOpen) {
      refreshIntervalRef.current = setInterval(refreshTable, 5000);
    }

    // Очищаем интервал при размонтировании компонента
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  };

  useEffect(() => {
    refreshIntervalList();

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isAnyModalOpen]);

  useEffect(() => {
    changePageNumber(0);
    refreshTable(true);
    refreshIntervalList();
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [params]);

  useEffect(() => {
    refreshTable();
    refreshIntervalList();
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [pageNumber]);

  const printLogs = (isPrint) => {
    if (isPrint) setPrintLoading(true);
    else setLoading(true);
    const filters = getFiltersObject(params);
    printLogsList({
      ...filters,
      pageNumber,
    }).then((data) => {
      setLoading(false);
      setPrintLoading(false);
      if (isPrint) printBase64(data.data.content);
      else base64toPDF(data.data.name, data.data.content);
    });
  };

  useEffect(() => {
    setIsErrorRequest(error);
  }, [error]);

  const renderHeader = useMemo(() => <EnhancedTableHead columns={columns} />, [columns]);

  return (
    <Box className="template_page magazine_events">
      <Box className="template_page-header">
        <Typography className="title-page">{t("title_page:magazines_events")}</Typography>
      </Box>
      <Box className="magazine_events-select_container">
        <FormItem
          control={control}
          name={`${formFields.searchParams}.${formFields.showOnlyFireMessagesPerActiveSession}`}
          type={"select-text"}
          options={eventsOptions}
          input={(props) => (
            <Box width={"400px"}>
              <SelectSimpleWithClientSearch
                writeSearchDataToSessionStorage={writeSearchDataToSessionStorage}
                storageValue="showOnlyFireMessagesPerActiveSessionStorage"
                {...props}
              />
            </Box>
          )}
        />
        <Box className="template_page-header">
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tooltip title="Печать" componentsProps={TooltipProps} placement="bottom" arrow>
              <Box className="full_screen_icon-wrapper">
                <Button onClick={() => printLogs(true)} className="btn btn-white">
                  {printLoading ? <CircularProgress size={24} color="inherit" /> : <PrintIcon />}
                </Button>
              </Box>
            </Tooltip>

            <Tooltip title="Очистить все фильтры" componentsProps={TooltipProps} placement="bottom" arrow>
              <Button className="btn btn-white" onClick={clearFiltersHandler}>
                <ClearFilterIcon />
              </Button>
            </Tooltip>

            <Tooltip title={t("button:create_act_pdf")} componentsProps={TooltipProps} placement="bottom" arrow>
              <Button className="btn btn-white" variant="outlined" onClick={() => printLogs(false)} disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
              </Button>
            </Tooltip>

            <Tooltip
              title={t("button:customize_fields_to_display")}
              componentsProps={TooltipProps}
              placement="bottom"
              arrow
            >
              <Button className="btn btn-white" variant="outlined" onClick={handleOpenSelectColumnsModal}>
                <SettingsIcon />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <TableBasic
        renderHeader={renderHeader}
        columns={columns}
        rows={rows}
        handleClickCell={() => {}}
        withSearchField={false}
        isLoading={isLoading}
        pageNumber={pageNumber}
        changePageNumber={changePageNumber}
        count={recordsCount}
        getCustomBgColorRow={getBgColor(bgColors)}
        getCustomBorderRow={getBorderColor()}
        minHeight={getTableHieght(screenSize, false, "350px").min}
        maxHeight={getTableHieght(screenSize, false, "350px").max}
      />

      {openSelectModal && (
        <ModalSelectColumnsServer
          open={openSelectModal}
          setOpen={setOpenSelectModal}
          data={columnsFromStorage}
          refetch={() => getUserGrid({ gridSettingName: "EventLogJournal" })}
          nameStorage={"EventLogJournal"}
          config={getConfigFormModalSelect(gridColumns?.columnSettings || [])}
        />
      )}
    </Box>
  );
};
